/**
 * Ops admin — uncertain matches between appellate opinions and DOC records.
 *
 * The matcher links an opinion to a DOC offender and docket on its own only
 * when county, docket and name all agree. The rest wait here. Each card puts
 * the opinion's sentence beside the DOC orders of every candidate, so the
 * reviewer can tell whether they are the same case:
 *
 *   Same case      the opinion enriches that DOC order (which still counts once)
 *   Not the same   the opinion's sentences stand as rows of their own, known from the opinion only
 *
 * A decision is appended, never overwritten; a later one supersedes it. The
 * sentencing dataset is shared, so a decision changes what every entitled
 * user sees — owner and admin roles only.
 *
 * Data: GET /ops_admin/sentencing/matches, POST …/:opinionId/decision
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '../../api';
import { OpinionLinks } from '../../components/SentencingOpinion';
import { PLACE_LABELS, formatMonths, formatTerm } from '../../domain/sentencing';
import '../../components/SentencingBrowse.css';

const PAGE = 10;

const WHY = {
  loose: 'the docket number agrees but its prefix or department letter does not',
  name_only: 'no usable cause number; found by name and county',
  exact: 'the docket agrees',
};

function whyUncertain(item) {
  const parts = [WHY[item.why_uncertain?.docket_match] || `docket: ${item.why_uncertain?.docket_match || 'unknown'}`];
  if (item.why_uncertain?.name_match === 'last') parts.push('only the last name agrees');
  if (item.why_uncertain?.name_match === 'none') parts.push('the name does not agree');
  if ((item.candidates || []).length > 1) parts.push(`${item.candidates.length} candidates`);
  return parts.join('; ');
}

function OpinionSide({ item }) {
  const d = (item.defendants || [])[0] || {};
  return (
    <div className="so-col">
      <div className="sb-detail-label">The opinion</div>
      <div className="so-stage-head">
        <strong>{d.name || item.opinion.caption_defendant}</strong>
        <span>{item.opinion.county || 'county not stated'}</span>
        <span className="sb-cell-mono">{(item.opinion.cause_numbers || []).join(', ') || 'no cause number'}</span>
        {(d.judge || item.opinion.caption_judge) && <span>Judge {d.judge || item.opinion.caption_judge}</span>}
        {d.sentencing_date_text && <span>pronounced {d.sentencing_date_text}</span>}
      </div>
      {(item.sentences || []).map((s) => (
        <div key={s.id} className="so-row">
          <span className="so-row-offense">
            {s.mca_section && <span className="sb-mca sb-mca-other">{s.mca_section}</span>} {s.offense || '—'}
          </span>
          <span className="so-row-amounts">
            {s.term_months != null ? formatMonths(s.term_months) : (PLACE_LABELS[s.commitment] || '—')}
            {s.suspended_months != null ? ` · ${formatMonths(s.suspended_months)} suspended` : ''}
          </span>
          <span className="so-row-fate so-muted">{s.sentence_stage}</span>
        </div>
      ))}
    </div>
  );
}

function Candidate({ candidate, canDecide, busy, onConfirm }) {
  return (
    <div className="so-stage">
      <div className="so-stage-head">
        <strong>{candidate.orders?.[0]?.defendant || `DOC ${candidate.doc_id}`}</strong>
        <span>DOC ID {candidate.doc_id}</span>
        <span>{candidate.county}</span>
        <span className="sb-cell-mono">{candidate.docket}</span>
        {candidate.judge && <span>Judge {candidate.judge}</span>}
      </div>
      {(candidate.orders || []).length === 0 && <div className="so-muted">No DOC order on this docket in the database.</div>}
      {(candidate.orders || []).map((o) => (
        <div key={o.id}>
          <div className="so-muted">
            {o.procedural_sentence_type || 'Sentence'}{o.imposed_on ? `, pronounced ${o.imposed_on}` : ''} · total {formatTerm(o.total_term)}
            {o.agg_sentence?.commit_type ? ` · ${o.agg_sentence.commit_type}` : ''}
          </div>
          {(o.counts || []).map((c) => (
            <div key={c.charge_index} className="so-row">
              <span className="so-row-offense">
                {c.mca_section && <span className="sb-mca sb-mca-other">{c.mca_section}</span>} {c.description || '—'}
              </span>
              <span className="so-row-amounts">{formatTerm(c.term)}</span>
              <span className="so-row-fate" />
            </div>
          ))}
        </div>
      ))}
      {canDecide && (candidate.orders || []).length > 0 && (
        <button className="btn btn-sm btn-primary" disabled={busy} onClick={onConfirm}>
          Same case
        </button>
      )}
    </div>
  );
}

// The review log holds a person's decisions and a model's; a model signs as "model:<name>".
function decidedBy(entry) {
  const who = String(entry.decided_by || '');
  return who.startsWith('model:') ? `a model (${who.slice(6)})` : who;
}

export default function SentencingMatches({ opsRole }) {
  const [pending, setPending] = useState(true);
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  const canDecide = ['owner', 'admin'].includes(opsRole);

  const load = useCallback(() => {
    setError(null);
    api.get('/ops_admin/sentencing/matches', { pending, limit: PAGE, offset })
      .then(setData)
      .catch((e) => setError(e.message || 'Failed to load'));
  }, [pending, offset]);

  useEffect(load, [load]);

  const decide = async (item, decision, candidateIndex) => {
    setBusy(item.opinion.opinion_id);
    setError(null);
    try {
      await api.post(`/ops_admin/sentencing/matches/${item.opinion.opinion_id}/decision`, {
        decision,
        candidate_index: decision === 'confirmed' ? candidateIndex : null,
      });
      load();
    } catch (e) {
      setError(e.message || 'Could not record the decision');
    } finally {
      setBusy(null);
    }
  };

  const items = data?.items || [];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, marginBottom: 8 }}>
        <h1 style={{ fontSize: 24 }}>Sentencing — opinion and DOC matches to review</h1>
        <select value={pending ? 'pending' : 'all'} onChange={(e) => { setOffset(0); setPending(e.target.value === 'pending'); }} style={{ padding: 4 }}>
          <option value="pending">Waiting for a decision</option>
          <option value="all">All, including decided</option>
        </select>
        {data && <span className="so-muted">{data.total.toLocaleString()} {pending ? 'waiting' : 'in the queue'}</span>}
      </div>
      <p className="so-muted" style={{ maxWidth: 820, marginBottom: 16 }}>
        Is the opinion about the same case as the DOC record? “Same case” attaches the opinion to that DOC record, which
        still counts once, known from both. “Not the same” leaves DOC untouched and lists the opinion’s sentences as rows of their own, known from the opinion only.
        Neither changes any figure; the opinion and DOC stay side by side. For now a model decides the matches it can
        and gives its reason; what it cannot tell waits here, and a person’s decision always replaces the model’s.
      </p>

      {error && <p style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {!data && !error && <p>Loading…</p>}
      {data && items.length === 0 && <p className="so-muted">Nothing {pending ? 'waiting' : 'here'}.</p>}

      {items.map((item) => {
        const id = item.opinion.opinion_id;
        const last = (item.history || [])[0];
        return (
          <div key={id} className="sb-detail" style={{ marginBottom: 14, background: 'var(--color-surface, #fff)' }}>
            <div className="sb-detail-case">
              <OpinionLinks opinion={item.opinion} />
              {item.opinion.case_name && <span>{item.opinion.case_name}</span>}
              {item.opinion.filed_date && <span>decided {item.opinion.filed_date}</span>}
              <span className="so-muted">Why it needs a look: {whyUncertain(item)}</span>
            </div>
            {last && (
              <div className="so-muted" style={{ marginBottom: 6 }}>
                Decided: {last.decision === 'confirmed' ? `same case as DOC ${last.doc_id} (${last.doc_docket})` : 'not the same case'}
                {' '}by {decidedBy(last)} on {String(last.decided_at).slice(0, 10)}
                {last.note ? ` — ${last.note}` : ''}
                {item.decided_by_kind === 'model' && canDecide ? ' A decision made here replaces it.' : ''}
              </div>
            )}
            <div className="so-sides">
              <OpinionSide item={item} />
              <div className="so-col">
                <div className="sb-detail-label">DOC candidate{(item.candidates || []).length === 1 ? '' : 's'}</div>
                {(item.candidates || []).map((c) => (
                  <Candidate
                    key={c.index}
                    candidate={c}
                    canDecide={canDecide}
                    busy={busy === id}
                    onConfirm={() => decide(item, 'confirmed', c.index)}
                  />
                ))}
              </div>
            </div>
            {canDecide && (
              <div style={{ marginTop: 8 }}>
                <button className="btn btn-sm btn-ghost" disabled={busy === id} onClick={() => decide(item, 'rejected')}>
                  Not the same case
                </button>
              </div>
            )}
          </div>
        );
      })}

      {data && data.total > PAGE && (
        <div className="sb-paging">
          <button className="btn btn-sm btn-ghost" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>← Prev</button>
          <span className="sb-page-label">{offset + 1}–{Math.min(offset + PAGE, data.total)} of {data.total}</span>
          <button className="btn btn-sm btn-ghost" disabled={offset + PAGE >= data.total} onClick={() => setOffset(offset + PAGE)}>Next →</button>
        </div>
      )}
    </div>
  );
}
